import React, { Component } from 'react';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import DashboardHeader from '@/components/Header/DashboardHeader';
import { Footer } from '@/components/organisms/Footer';
import styles from './BarDashboard.module.scss';
import { apiService } from '@/services/api';

interface Match {
  id: string;
  title: string;
  gameType: string;
  players: number;
  maxPlayers: number;
  prize: string;
  entryFee: string;
  startTime: string;
  status: 'upcoming' | 'active' | 'completed';
  originalStatus?: string; // Store original status from API
  tableNumber: number;
  skillLevel: 'beginner' | 'intermediate' | 'advanced' | 'professional';
  registrationDeadline?: string; // ISO date string
  isRegistrationOpen?: boolean; // Whether registration is currently open
  isCustomerRegistered?: boolean; // Whether the current customer is registered for this tournament
}

interface BarDetails {
  id: string;
  name: string;
  location: string;
  rating: number;
  image: string;
  description: string;
  facilities: string[];
  operatingHours: string;
  contact: string;
  address: string;
}

interface BarDashboardState {
  activeTab: 'scheduled' | 'history';
  organizer: BarDetails | null;
  tournaments: Match[];
  loading: boolean;
  error?: string;
  registeringTournaments: Set<string>; // Track tournaments being registered
  registeredTournaments: Set<string>; // Track successfully registered tournaments
  registrationErrors: { [tournamentId: string]: string }; // Track registration errors
  showSuccessModal: boolean; // Show success modal after registration
  successMessage?: string; // Success message to display
  registrationId?: string; // Registration ID
}

// Higher-order component to provide navigation and params
function withNavigation(WrappedComponent: any) {
  return function WithNavigationComponent(props: any) {
    const navigate = useNavigate();
    const params = useParams();
    const location = useLocation();
    return <WrappedComponent {...props} navigate={navigate} params={params} location={location} />;
  };
}

class BarDashboard extends Component<any, BarDashboardState> {
  constructor(props: any) {
    super(props);
    this.state = {
      activeTab: 'scheduled',
      organizer: null,
      tournaments: [],
      loading: true,
      registeringTournaments: new Set(),
      registeredTournaments: new Set(),
      registrationErrors: {},
      showSuccessModal: false,
      successMessage: undefined,
      registrationId: undefined
    };
  }

  async componentDidMount() {
    const organizerId = this.props.params.organizerId as string;
    
    // Note: We don't load stored registrations from localStorage on mount
    // to avoid showing incorrect "Registered" status
    // Registration status is only determined when user clicks register button
    
    try {
      const tourRes = await apiService.getOrganizerTournaments(organizerId);

      console.log('[BarDashboard] Location state:', this.props.location?.state);
      console.log('[BarDashboard] Organizer tournaments raw:', tourRes);

      const fromState = (this.props.location?.state as any)?.organizer;
      const organizer: BarDetails = fromState ? {
        id: organizerId,
        name: fromState.name || 'Organizer',
        location: fromState.location || 'Unknown',
        rating: fromState.rating || 4.5,
        image: fromState.image || 'https://images.unsplash.com/photo-1551882547-ff40c63fe5fa?w=600&h=300&fit=crop&auto=format',
        description: fromState.description || 'Billiards organizer',
        facilities: Array.isArray(fromState.facilities) ? fromState.facilities : ['8-Foot Tables', '9-Foot Tables', 'Snooker Tables'],
        operatingHours: fromState.operatingHours || 'Mon-Sun: 10:00 AM - 2:00 AM',
        contact: fromState.contact || '',
        address: fromState.address || '—'
      } : {
        id: organizerId,
        name: 'Organizer',
        location: 'Unknown',
        rating: 4.5,
        image: 'https://images.unsplash.com/photo-1551882547-ff40c63fe5fa?w=600&h=300&fit=crop&auto=format',
        description: 'Billiards organizer',
        facilities: ['8-Foot Tables', '9-Foot Tables', 'Snooker Tables'],
      operatingHours: 'Mon-Sun: 10:00 AM - 2:00 AM',
        contact: '',
        address: '—'
      };

      let tournamentsContainer: any = (tourRes && (tourRes as any).data) ?? tourRes;
      let tournamentsRaw: any = Array.isArray(tournamentsContainer)
        ? tournamentsContainer
        : (
            tournamentsContainer?.tournaments ||
            tournamentsContainer?.items ||
            tournamentsContainer?.rows ||
            tournamentsContainer?.list ||
            tournamentsContainer?.results ||
            tournamentsContainer?.data
          );

      if (!Array.isArray(tournamentsRaw)) {
        const firstArray = tournamentsContainer && Object.values(tournamentsContainer).find((v: any) => Array.isArray(v));
        tournamentsRaw = Array.isArray(firstArray) ? firstArray : [];
      }
      const tournaments: Match[] = tournamentsRaw.map((t: any, idx: number) => {
        // Log tournament object for debugging
        if (idx === 0) {
          console.log('[BarDashboard] Sample tournament object:', t);
          console.log('[BarDashboard] Tournament object keys:', Object.keys(t));
        }
        
        // Format prize amount
        const prizeAmount = t.prize || t.prize_pool || t.prizePool || 0;
        const prize = prizeAmount ? (typeof prizeAmount === 'number' ? `$${prizeAmount}` : String(prizeAmount)) : '$0';
        
        // Format entry fee
        const feeAmount = t.entry_fee || t.entryFee || t.fee || 0;
        const entryFee = feeAmount ? (typeof feeAmount === 'number' ? `$${feeAmount}` : String(feeAmount)) : '$0';
        
        // Format start time
        let startTime = t.start_time || t.startTime || t.start_at || t.startDate || t.start_date || 'TBD';
        if (startTime && startTime !== 'TBD' && typeof startTime === 'string') {
          try {
            const date = new Date(startTime);
            if (!isNaN(date.getTime())) {
              startTime = date.toLocaleString();
            }
          } catch (e) {
            // Keep original format if parsing fails
          }
        }
        
        // Normalize status - be more permissive, only mark as completed if explicitly completed
        const originalStatus = (t.status || 'upcoming').toLowerCase();
        let status: 'upcoming' | 'active' | 'completed' = 'upcoming';
        
        // Only mark as completed if explicitly completed/finished/ended
        if (originalStatus === 'completed' || originalStatus === 'finished' || originalStatus === 'ended' || originalStatus === 'closed') {
          status = 'completed';
        } else if (originalStatus === 'in_progress' || originalStatus === 'live' || originalStatus === 'started' || originalStatus === 'running' || originalStatus === 'ongoing') {
          status = 'active';
        } else {
          // Default all other statuses (scheduled, registration_open, upcoming, etc.) to upcoming
          status = 'upcoming';
        }
        
        // Get tournament title from various possible field names
        // Prioritize tournament_name as it's the primary field in the database
        const tournamentTitle = t.tournament_name || t.tournamentName || t.title || t.name || 
                                t.tournament_title || t.title_name || t.event_name || t.match_name || 
                                (t.data && (t.data.tournament_name || t.data.title || t.data.name)) || 'Tournament';
        
        if (idx === 0) {
          console.log('[BarDashboard] Tournament title mapping:', {
            tournament_name: t.tournament_name,
            tournamentName: t.tournamentName,
            title: t.title,
            name: t.name,
            resolved: tournamentTitle
          });
        }
        
        // Get registration deadline
        const registrationDeadline = t.registration_deadline || t.registrationDeadline || t.deadline || null;
        
        // Get registration open status from API
        // Check both snake_case and camelCase variants
        const isRegistrationOpen = t.is_registration_open !== undefined 
          ? Boolean(t.is_registration_open) 
          : t.isRegistrationOpen !== undefined 
            ? Boolean(t.isRegistrationOpen) 
            : undefined;
        
        // Get customer registration status from API
        // Check both snake_case and camelCase variants
        const isCustomerRegistered = t.is_customer_registered !== undefined 
          ? Boolean(t.is_customer_registered) 
          : t.isCustomerRegistered !== undefined 
            ? Boolean(t.isCustomerRegistered) 
            : undefined;
        
        // Log for debugging (only for first tournament)
        if (idx === 0) {
          console.log('[BarDashboard] Registration status check:', {
            is_registration_open: t.is_registration_open,
            isRegistrationOpen: t.isRegistrationOpen,
            resolvedIsOpen: isRegistrationOpen,
            is_customer_registered: t.is_customer_registered,
            isCustomerRegistered: t.isCustomerRegistered,
            resolvedIsRegistered: isCustomerRegistered
          });
        }
        
        return {
          id: t.id?.toString?.() || t.tournament_id?.toString?.() || `${idx + 1}`,
          title: tournamentTitle,
          gameType: t.game_type || t.gameType || t.format || t.game_format || '8-Ball',
          players: Number(t.registered_players || t.registeredPlayers || t.players || t.current_players || 0),
          maxPlayers: Number(t.max_players || t.maxPlayers || t.capacity || t.max_capacity || t.total_slots || 16),
          prize,
          entryFee,
          startTime: String(startTime),
          status: status as 'upcoming' | 'active' | 'completed',
          originalStatus: originalStatus, // Store original status from API
          tableNumber: Number(t.table_number || t.tableNumber || t.table || 1),
          skillLevel: (t.skill_level || t.skillLevel || t.difficulty_level || 'intermediate').toLowerCase() as 'beginner' | 'intermediate' | 'advanced' | 'professional',
          registrationDeadline: registrationDeadline ? String(registrationDeadline) : undefined,
          isRegistrationOpen,
          isCustomerRegistered
        };
      });

      console.log('[BarDashboard] Parsed tournaments count:', tournaments.length, tournaments);
      
      // Extract registration status from API response
      // Use is_customer_registered from API if available
      const registeredTournaments = new Set<string>();
      tournaments.forEach(tournament => {
        if (tournament.isCustomerRegistered === true) {
          registeredTournaments.add(tournament.id);
          // Store in localStorage for persistence
          this.storeRegistration(tournament.id);
        }
      });
      
      this.setState({ 
        organizer, 
        tournaments, 
        loading: false,
        registeredTournaments: registeredTournaments.size > 0 ? registeredTournaments : this.state.registeredTournaments
      });

      // Note: Registration status is now determined from API response (is_customer_registered field)
      // No need to make additional API calls to check registration status
    } catch (error: any) {
      console.error('[BarDashboard] Failed to load organizer/tournaments:', error);
      this.setState({ loading: false, error: error?.message || 'Failed to load data' });
    }
  }

  // Removed loadStoredRegistrations - we don't load from localStorage on mount
  // to avoid showing incorrect registration status

  storeRegistration = (tournamentId: string) => {
    // Store registration in localStorage
    try {
      const stored = localStorage.getItem('customer_tournament_registrations');
      let tournamentIds: string[] = stored ? JSON.parse(stored) : [];
      if (!Array.isArray(tournamentIds)) {
        tournamentIds = [];
      }
      if (!tournamentIds.includes(tournamentId)) {
        tournamentIds.push(tournamentId);
        localStorage.setItem('customer_tournament_registrations', JSON.stringify(tournamentIds));
      }
    } catch (error) {
      console.log('[BarDashboard] Could not store registration:', error);
    }
  }



  getStatusColor = (status: string) => {
    switch (status) {
      case 'upcoming': return '#3b82f6';
      case 'active': return '#22c55e';
      case 'completed': return '#6b7280';
      default: return '#6b7280';
    }
  };

  getSkillLevelColor = (level: string) => {
    switch (level) {
      case 'beginner': return '#22c55e';
      case 'intermediate': return '#f59e0b';
      case 'advanced': return '#ef4444';
      case 'professional': return '#8b5cf6';
      default: return '#6b7280';
    }
  };


  handleJoinMatch = async (matchId: string, entryFee?: string) => {
    // Check if already registered
    if (this.state.registeredTournaments.has(matchId)) {
      return;
    }

    // Check if already registering
    if (this.state.registeringTournaments.has(matchId)) {
      return;
    }

    // Check authentication
    if (!apiService.isAuthenticated()) {
      this.props.navigate('/login');
      return;
    }

    // Extract payment amount from entry fee string (e.g., "$25" -> 25)
    const paymentAmount = entryFee ? parseFloat(entryFee.replace(/[^0-9.]/g, '')) : undefined;

    // Add to registering set
    this.setState(prevState => ({
      registeringTournaments: new Set(prevState.registeringTournaments).add(matchId),
      registrationErrors: { ...prevState.registrationErrors, [matchId]: '' }
    }));

    try {
      const response = await apiService.registerForTournament(matchId, {
        paymentAmount,
        paymentMethod: 'upi',
        paymentStatus: 'pending'
      });

      if (response.success && response.data) {
        const registrationData = response.data as any;
        const isAlreadyRegistered = registrationData.alreadyRegistered === true;
        
        // Registration successful (new or already registered)
        this.setState(prevState => {
          const newRegistered = new Set(prevState.registeredTournaments).add(matchId);
          const newRegistering = new Set(prevState.registeringTournaments);
          newRegistering.delete(matchId);
          const newErrors = { ...prevState.registrationErrors };
          delete newErrors[matchId];
          
          // Store registration in localStorage
          this.storeRegistration(matchId);
          
          return {
            registeredTournaments: newRegistered,
            registeringTournaments: newRegistering,
            registrationErrors: newErrors
          };
        });

        // Update tournament players count only if it's a new registration
        if (!isAlreadyRegistered) {
          this.setState(prevState => ({
            tournaments: prevState.tournaments.map(t => 
              t.id === matchId 
                ? { ...t, players: t.players + 1 }
                : t
            )
          }));
        }

        console.log('[BarDashboard] Registration response:', {
          isAlreadyRegistered,
          registrationData
        });
        
        // Show success modal with appropriate message
        this.setState({
          showSuccessModal: true,
          successMessage: isAlreadyRegistered
            ? 'You are already registered for this tournament'
            : 'Successfully registered for tournament!',
          registrationId: registrationData.registrationId || undefined
        });
      } else {
        throw new Error(response.message || 'Registration failed');
      }
    } catch (error: any) {
      let errorMessage = error?.response?.data?.message || error?.message || 'Failed to register for tournament. Please try again.';
      
      // Handle specific error cases
      if (errorMessage.includes('already registered') || errorMessage.includes('Already registered')) {
        // API returns 200 OK with alreadyRegistered flag, but handle as fallback
        console.log('[BarDashboard] Customer is already registered for tournament:', matchId);
        
        // Mark as registered in state so UI updates
        this.setState(prevState => {
          const newRegistered = new Set(prevState.registeredTournaments).add(matchId);
          const newRegistering = new Set(prevState.registeringTournaments);
          newRegistering.delete(matchId);
          const newErrors = { ...prevState.registrationErrors };
          delete newErrors[matchId];
          
          // Store registration in localStorage
          this.storeRegistration(matchId);
          
          return {
            registeredTournaments: newRegistered,
            registeringTournaments: newRegistering,
            registrationErrors: newErrors
          };
        });
        
        // Show info message instead of error
        this.setState({
          showSuccessModal: true,
          successMessage: 'You are already registered for this tournament'
        });
        return;
      }
      
      // Log actual errors (not already registered cases)
      console.error('[BarDashboard] Registration failed:', error);
      
      // Handle other specific error cases
      if (errorMessage.includes('deadline has passed')) {
        errorMessage = 'Registration deadline has passed for this tournament.';
      } else if (errorMessage.includes('not accepting registrations')) {
        errorMessage = 'This tournament is not currently accepting registrations.';
      } else if (errorMessage.includes('Tournament is full')) {
        errorMessage = 'This tournament is full. No more registrations accepted.';
      } else if (errorMessage.includes('Tournament not found')) {
        errorMessage = 'Tournament not found. It may have been cancelled.';
      }
      
      this.setState(prevState => {
        const newRegistering = new Set(prevState.registeringTournaments);
        newRegistering.delete(matchId);
        
        return {
          registeringTournaments: newRegistering,
          registrationErrors: {
            ...prevState.registrationErrors,
            [matchId]: errorMessage
          }
        };
      });
    }
  };

  renderStars = (rating: number) => {
    const stars = [];
    const fullStars = Math.floor(rating);
    const hasHalfStar = rating % 1 !== 0;

    for (let i = 0; i < fullStars; i++) {
      stars.push(<span key={i} className={styles.star}>★</span>);
    }
    if (hasHalfStar) {
      stars.push(<span key="half" className={styles.starHalf}>★</span>);
    }
    const emptyStars = 5 - Math.ceil(rating);
    for (let i = 0; i < emptyStars; i++) {
      stars.push(<span key={`empty-${i}`} className={styles.starEmpty}>★</span>);
    }
    return stars;
  };

  render() {
    const { activeTab, tournaments, loading } = this.state;
    
    // Show ALL tournaments in scheduled tab - no filtering
    // History tab shows all tournaments too for now
    const scheduledMatches = tournaments;
    const previousMatches = tournaments;
    
    console.log('[BarDashboard] Total tournaments:', tournaments.length);
    console.log('[BarDashboard] Scheduled matches (all):', scheduledMatches.length);
    console.log('[BarDashboard] Tournament statuses:', tournaments.map(t => ({ id: t.id, title: t.title, status: t.status })));
    
    if (loading) {
      return (
        <>
          <DashboardHeader />
          <div className={styles.barDashboardContainer}>
            <div className={styles.barDashboardContent}>
              <p>Loading organizer details...</p>
            </div>
          </div>
        </>
      );
    }
    
    return (
      <>
        <DashboardHeader />
        <div className={styles.barDashboardContainer}>
          <div className={styles.barDashboardContent}>
            {/* Back Button */}
            <button 
              onClick={() => this.props.navigate('/customer/friends')}
              className={styles.backButton}
            >
              ← Back to All Bars
            </button>

          {/* Bar Header */}
          <div className={styles.barHeader}>
            <div className={styles.barImage}>
              <img src={this.state.organizer?.image || 'https://images.unsplash.com/photo-1551882547-ff40c63fe5fa?w=600&h=300&fit=crop&auto=format'} alt={this.state.organizer?.name || 'Organizer'} />
            </div>
            <div className={styles.barInfo}>
              <h1 className={styles.barName}>{this.state.organizer?.name || 'Organizer'}</h1>
              <div className={styles.barRating}>
                {this.renderStars(this.state.organizer?.rating || 4.5)}
                <span className={styles.ratingText}>({this.state.organizer?.rating || 4.5})</span>
              </div>
              <div className={styles.barLocation}>
                <span className={styles.locationIcon}>📍</span>
                {this.state.organizer?.location || 'Unknown'}
              </div>
              <p className={styles.barDescription}>{this.state.organizer?.description}</p>
              
              <div className={styles.barDetails}>
                <div className={styles.detailRow}>
                  <span className={styles.detailLabel}>Address:</span>
                  <span className={styles.detailValue}>{this.state.organizer?.address || '—'}</span>
                </div>
                <div className={styles.detailRow}>
                  <span className={styles.detailLabel}>Hours:</span>
                  <span className={styles.detailValue}>{this.state.organizer?.operatingHours || 'Mon-Sun: 10:00 AM - 2:00 AM'}</span>
                </div>
                <div className={styles.detailRow}>
                  <span className={styles.detailLabel}>Contact:</span>
                  <span className={styles.detailValue}>{this.state.organizer?.contact || '—'}</span>
                </div>
              </div>

              <div className={styles.facilities}>
                <h3>Facilities:</h3>
                <div className={styles.facilitiesList}>
                  {(this.state.organizer?.facilities || []).map((facility, index) => (
                    <span key={index} className={styles.facility}>
                      {facility}
                    </span>
                  ))}
                </div>
              </div>
            </div>
          </div>

          {/* Tab Navigation */}
          <div className={styles.tabNavigation}>
            <button
              className={`${styles.tabButton} ${activeTab === 'scheduled' ? styles.active : ''}`}
              onClick={() => this.setState({ activeTab: 'scheduled' })}
            >
              Scheduled Matches ({scheduledMatches.length})
            </button>
            <button
              className={`${styles.tabButton} ${activeTab === 'history' ? styles.active : ''}`}
              onClick={() => this.setState({ activeTab: 'history' })}
            >
              Match History ({previousMatches.length})
            </button>
          </div>

          {/* Scheduled Matches */}
          {activeTab === 'scheduled' && (
            <div className={styles.matchesSection}>
              <div className={styles.matchesGrid}>
                {scheduledMatches.length > 0 ? scheduledMatches.map((match) => (
                  <div key={match.id} className={styles.matchCard}>
                    <div className={styles.matchHeader}>
                      <div className={styles.matchTitle}>
                        <h3>{match.title}</h3>
                        <span 
                          className={styles.gameType}
                          style={{ backgroundColor: this.getSkillLevelColor(match.skillLevel) }}
                        >
                          {match.gameType}
                        </span>
                      </div>
                      <div 
                        className={styles.statusBadge}
                        style={{ backgroundColor: this.getStatusColor(match.status) }}
                      >
                        {match.status.toUpperCase()}
                      </div>
                    </div>

                    <div className={styles.matchDetails}>
                      <div className={styles.detailRow}>
                        <span className={styles.detailLabel}>Players:</span>
                        <span className={styles.detailValue}>
                          {match.players}/{match.maxPlayers}
                        </span>
                      </div>
                      
                      <div className={styles.detailRow}>
                        <span className={styles.detailLabel}>Prize:</span>
                        <span className={styles.prizeValue}>{match.prize}</span>
                      </div>
                      
                      <div className={styles.detailRow}>
                        <span className={styles.detailLabel}>Entry Fee:</span>
                        <span className={styles.entryFee}>{match.entryFee}</span>
                      </div>
                      
                      <div className={styles.detailRow}>
                        <span className={styles.detailLabel}>Start Time:</span>
                        <span className={styles.detailValue}>{match.startTime}</span>
                      </div>
                      
                      <div className={styles.detailRow}>
                        <span className={styles.detailLabel}>Table:</span>
                        <span className={styles.detailValue}>#{match.tableNumber}</span>
                      </div>
                      
                      <div className={styles.detailRow}>
                        <span className={styles.detailLabel}>Skill Level:</span>
                        <span 
                          className={styles.skillLevel}
                          style={{ color: this.getSkillLevelColor(match.skillLevel) }}
                        >
                          {match.skillLevel.toUpperCase()}
                        </span>
                      </div>
                    </div>

                    <div className={styles.matchActions}>
                      {/* Show Watch Tournament button for all tournaments */}
                      <button 
                        className={styles.watchButton}
                        onClick={() => {
                          // Navigate to tournament details/view page
                          this.props.navigate(`/tournament/${match.id}`);
                        }}
                      >
                        {match.status === 'active' || match.originalStatus === 'started' || 
                         match.originalStatus === 'running' || match.originalStatus === 'ongoing' ||
                         match.originalStatus === 'in_progress' || match.originalStatus === 'live'
                          ? '🔴 Watch Tournament' : '📺 View Tournament'}
                      </button>

                      {/* Show registration section only for upcoming tournaments */}
                      {match.status !== 'active' && match.status !== 'completed' && (
                        <>
                          {/* Registration status description */}
                          {match.isRegistrationOpen === true && (
                            <div className={styles.registrationStatus} style={{ color: '#22c55e', fontSize: '0.875rem', marginBottom: '8px', fontWeight: '500' }}>
                              ✓ Accepting registrations...
                            </div>
                          )}
                          {match.isRegistrationOpen === false && (
                            <div className={styles.registrationStatus} style={{ color: '#ef4444', fontSize: '0.875rem', marginBottom: '8px', fontWeight: '500' }}>
                              ✗ Registration closed
                            </div>
                          )}
                          
                          {this.state.registrationErrors[match.id] && (
                            <div className={styles.errorMessage} style={{ color: '#ef4444', fontSize: '0.875rem', marginBottom: '8px' }}>
                              {this.state.registrationErrors[match.id]}
                            </div>
                          )}
                          
                          {/* Only show register button if registration is explicitly open (true) */}
                          {match.isRegistrationOpen === true ? (
                            <button 
                              className={styles.joinButton}
                              onClick={() => this.handleJoinMatch(match.id, match.entryFee)}
                              disabled={
                                match.players >= match.maxPlayers || 
                                this.state.registeringTournaments.has(match.id) ||
                                this.state.registeredTournaments.has(match.id)
                              }
                            >
                              {this.state.registeredTournaments.has(match.id) 
                                ? '✓ Registered' 
                                : this.state.registeringTournaments.has(match.id)
                                ? 'Registering...'
                                : match.players >= match.maxPlayers 
                                ? 'Full' 
                                : 'Register for Tournament'}
                            </button>
                          ) : (
                            // If registration is not open (false or undefined), don't show register button
                            // Only show registered status if user is already registered
                            this.state.registeredTournaments.has(match.id) && (
                              <button 
                                className={styles.joinButton}
                                disabled={true}
                                style={{ backgroundColor: '#22c55e', cursor: 'not-allowed' }}
                              >
                                ✓ Registered
                              </button>
                            )
                          )}
                        </>
                      )}
                    </div>
                  </div>
                )) : (
                  <div className={styles.noMatches}>
                    <p>No scheduled matches available</p>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Match History */}
          {activeTab === 'history' && (
            <div className={styles.historySection}>
              <div className={styles.historyGrid}>
                {previousMatches.length > 0 ? previousMatches.map((match) => (
                  <div key={match.id} className={styles.historyCard}>
                    <div className={styles.historyHeader}>
                      <h3>{match.title}</h3>
                      <span 
                        className={styles.gameType}
                        style={{ backgroundColor: this.getSkillLevelColor(match.skillLevel) }}
                      >
                        {match.gameType}
                      </span>
                    </div>
                    
                    <div className={styles.historyDetails}>
                      <div className={styles.historyRow}>
                        <span className={styles.historyLabel}>Players:</span>
                        <span className={styles.historyValue}>{match.players}</span>
                      </div>
                      
                      <div className={styles.historyRow}>
                        <span className={styles.historyLabel}>Prize:</span>
                        <span className={styles.prizeWon}>{match.prize}</span>
                      </div>
                      
                      <div className={styles.historyRow}>
                        <span className={styles.historyLabel}>Entry Fee:</span>
                        <span className={styles.historyValue}>{match.entryFee}</span>
                      </div>
                      
                      <div className={styles.historyRow}>
                        <span className={styles.historyLabel}>Status:</span>
                        <span className={styles.completedStatus}>COMPLETED</span>
                      </div>
                    </div>
                  </div>
                )) : (
                  <div className={styles.noMatches}>
                    <p>No match history available</p>
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      </div>
      <Footer />

      {/* Success Modal */}
      {this.state.showSuccessModal && (
        <div className={styles.successModalOverlay} onClick={() => this.setState({ showSuccessModal: false })}>
          <div className={styles.successModalContent} onClick={(e) => e.stopPropagation()}>
            <div className={styles.successIcon}>✓</div>
            <h2 className={styles.successTitle}>Registration Successful!</h2>
            <p className={styles.successMessage}>{this.state.successMessage}</p>
            {this.state.registrationId && (
              <div className={styles.registrationIdContainer}>
                <span className={styles.registrationIdLabel}>Registration ID:</span>
                <span className={styles.registrationId}>{this.state.registrationId}</span>
              </div>
            )}
            <button 
              className={styles.successButton}
              onClick={() => this.setState({ showSuccessModal: false, successMessage: undefined, registrationId: undefined })}
            >
              Continue
            </button>
          </div>
        </div>
      )}
    </>
    );
  }
}

export default withNavigation(BarDashboard);
